/**
 * UserAvatar — single source of truth for rendering a user's photo
 * (or initials fallback) anywhere in the app.
 *
 * Why this component exists:
 *   Prior to Session 68 pt3 every list/kanban/conversation rolled
 *   its own initial-letter circle, so the `users.avatar` upload from
 *   ProfilePage went unused outside the profile screen itself. This
 *   component centralises the photo→initials fallback so the sweep
 *   stays consistent across surfaces (task pills, kanban cards,
 *   support messages, mention chips, etc.).
 *
 * Inputs are deliberately permissive — we accept either:
 *   - a `user` object: { id, name, avatar, email }
 *   - or split props: { name, avatar, ... }
 *   so calling sites don't have to construct a wrapper for transient
 *   shapes returned by joins (e.g. tasks.assignee_name without a full
 *   user row).
 *
 * Sizes match the existing usage:
 *   xs  → 20px  (mention chips, table cells)
 *   sm  → 24px  (task pills, kanban small)
 *   md  → 32px  (kanban large, list rows)
 *   lg  → 40px  (ticket message bubbles, modal headers)
 *   xl  → 64px  (profile/team cards)
 *
 * Behaviour:
 *   - When `avatar` is set, render an <img> with `object-cover`.
 *   - When `avatar` 404s at runtime, fall back to the initials state
 *     (handled via onError → setBroken).
 *   - When no avatar is available, render the initial letter on a
 *     deterministic colour derived from the name hash so two people
 *     with the same first letter are still visually distinguishable.
 */
import { useState } from "react";
import { resolveBackendFileUrl } from "@/lib/fileUrls";

const SIZE_CLASSES = {
  xs: "w-5 h-5 text-[8px]",
  sm: "w-6 h-6 text-[10px]",
  md: "w-8 h-8 text-xs",
  lg: "w-10 h-10 text-sm",
  xl: "w-16 h-16 text-xl",
};

// Stable palette — every entry pairs a soft background with a darker
// foreground so the initial letter always reads cleanly. The name
// hash maps deterministically into this list so a given user keeps
// the same colour across renders.
const COLOR_PAIRS = [
  ["bg-[#F0F7FF]", "text-[#0055FF]"],
  ["bg-[#FEF3C7]", "text-[#92400E]"],
  ["bg-[#F0FDF4]", "text-[#16A34A]"],
  ["bg-[#FCE7F3]", "text-[#BE185D]"],
  ["bg-[#FFE4E6]", "text-[#BE123C]"],
  ["bg-[#EDE9FE]", "text-[#6D28D9]"],
  ["bg-[#E0F2FE]", "text-[#0369A1]"],
  ["bg-[#FFF7ED]", "text-[#C2410C]"],
];

function pickColor(seed) {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  }
  return COLOR_PAIRS[Math.abs(hash) % COLOR_PAIRS.length];
}

function getInitials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map(p => p.charAt(0).toUpperCase()).join("") || "?";
}

export default function UserAvatar({
  user,
  name,
  avatar,
  size = "md",
  className = "",
  ringClassName = "",
  testId,
}) {
  const [broken, setBroken] = useState(false);

  const resolvedName = user?.name ?? name ?? user?.email ?? "";
  const resolvedAvatar = resolveBackendFileUrl(user?.avatar ?? avatar ?? "");
  const sizeCls = SIZE_CLASSES[size] || SIZE_CLASSES.md;
  const seed = resolvedName || (user?.id || "?");
  const [bg, fg] = pickColor(seed);
  const initials = getInitials(resolvedName);
  const baseCls = `${sizeCls} flex-shrink-0 inline-flex items-center justify-center font-bold ${ringClassName} ${className}`;

  if (resolvedAvatar && !broken) {
    return (
      <img
        src={resolvedAvatar}
        alt={resolvedName || "avatar"}
        title={resolvedName || undefined}
        className={`${baseCls} object-cover bg-white`}
        onError={() => setBroken(true)}
        data-testid={testId}
      />
    );
  }

  return (
    <div
      className={`${baseCls} ${bg} ${fg}`}
      title={resolvedName || undefined}
      data-testid={testId}
    >
      {initials}
    </div>
  );
}
